import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { FetchWasteReport } from "../../ApiServices/ApiRequests";
import {
  BinOverviewReportInfoResponse,
  BinReportInfo,
  SitesInfo,
} from "../../Models/Models";
import { wastecolumns, getRowClassName } from "./WasteOverviewUtiles";
import {
  useWasteManagement,
  WasteManagementType,
} from "../../ContextAPI/WasteManagementContext";
import dayjs from "dayjs";
import ExportWastePage from "./ReportExport/WasteExport";
import WasteMonitoringCard from "./ReportCards/WasteReportCard";
import HeaderComponent from "../HeaderComponent/HeaderComponent";
import { useMasterData } from "../../ContextAPI/MasterContext";
import Loading from "../../Controller/LoadingController/Loading";

export default function WasteReport() {
  const { masterState } = useMasterData();
  const { wasteManagementState, dispatchWasteManagement } =
    useWasteManagement();

  const { siteData, pageSize, pageNo } = wasteManagementState;
  const [loading, setloading] = useState<boolean>(false);
  const handleInputChange = useCallback(
    (
      field: keyof WasteManagementType,
      value: BinReportInfo[] | number | boolean
    ) => {
      dispatchWasteManagement({ type: "SET_FIELD", field, value });
    },
    [dispatchWasteManagement]
  );

  const handleBulkUpdate = useCallback(
    (value: Partial<WasteManagementType>) => {
      dispatchWasteManagement({ type: "BULK_UPDATE", value });
    },
    [dispatchWasteManagement]
  );

  const { t } = useTranslation();

  const fetchData = async (
    siteCode: string,
    pageNo: number,
    pageSize: number,
    fromDate: string,
    groupId: string
  ): Promise<void> => {
    try {
      const data: BinOverviewReportInfoResponse = await FetchWasteReport(
        siteCode,
        pageNo,
        pageSize,
        fromDate,
        groupId
      );

      // Check if data.content has entries, and if so, process the report data
      const reportData =
        data.content && data.content.length > 0
          ? {
              siteData:
                data.content.map((item, index) => ({
                  ...item,
                  id: index,
                })) || [], // Ensure siteData is an empty array if report is undefined
            }
          : {
              siteData: [], // Set siteData as an empty array if content is empty
            };

      var wasteDataSet: Partial<WasteManagementType> = {};
      wasteDataSet.siteData = reportData.siteData;
      wasteDataSet.totalElements = data.totalElements;

      handleBulkUpdate(wasteDataSet);
    } catch (error) {
      console.error("Error loading site data", error);
    } finally {
      handleInputChange("isloading", false);
    }
  };

  useEffect(() => {
    const formattedDate: string = masterState.reportSelectedDate
      ? masterState.reportSelectedDate.format("YYYY-MM-DD")
      : "";

    if (masterState.selectedSiteCode.length > 0) {
      fetchData(
        masterState.selectedSiteCode,
        wasteManagementState.pageNo,
        wasteManagementState.pageSize,
        formattedDate,
        masterState.selectedGroupCode
      );
    }
  }, [
    masterState.selectedSiteCode,
    masterState.selectedGroupCode,
    masterState.reportSelectedDate,
    wasteManagementState.pageNo,
    wasteManagementState.pageSize,
  ]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "deviceName",
        headerName: t("global.deviceName"),
        width: 150,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },
      {
        field: "location",
        headerName: t("global.Location"),
        width: 200,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },
      {
        field: "groupName",
        headerName: t("global.GroupName"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
        // renderCell: (params) => (params.value === true ? "Yes" : "No"),
      },
      {
        field: "detectedObject",
        headerName: t("global.detectedObject"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
        //   renderCell: (params) => {
        //     let color;
        //     switch (params.value) {
        //       case "Clean":
        //         color = "green";
        //         break;
        //       case "Critical":
        //         color = "red";
        //         break;
        //       case "moderate":
        //         color = "orange";
        //         break;
        //       default:
        //         color = "inherit";
        //     }

        //     return (
        //       <span style={{ color }}>
        //         {params.value}
        //       </span>
        //     );
        //   },
      },
      {
        field: "level",
        headerName: t("global.FillLevel(%)"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
        renderCell: (params) =>
          params.value || params.value === 0 ? `${params.value}%` : "-",
      },
      {
        field: "lastCollectedDate",
        headerName: t("global.LastCollected"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },

      {
        field: "nextCollectionDate",
        headerName: t("global.NextCollected"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },

      {
        field: "actionTaken",
        headerName: t("global.ActionTaken"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },
    ],
    []
  );
  return (
    <Box sx={{ width: "100%",mt :10.5 }}>
      <HeaderComponent />
      <WasteMonitoringCard />
      <ExportWastePage />
      {loading ? (
        <Loading loading={loading} type={`dots`} backDrop={false} />
      ) : (
        <Box className="data-grid-container">
          <DataGrid
            rows={siteData}
            disableAutosize
            disableRowSelectionOnClick
            disableColumnResize
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableColumnSorting
            disableColumnMenu
            columns={columns}
            localeText={{
              noRowsLabel: "No Data Found",
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
              },
            }}
            getRowClassName={getRowClassName}
            paginationMode="server" // Enable server-side pagination
            paginationModel={{ pageSize, page: pageNo }} // Use paginationModel
            onPaginationModelChange={(model) => {
              handleInputChange("pageSize", model.pageSize);
              handleInputChange("pageNo", model.page);
            }}
            rowCount={wasteManagementState.totalElements}
            pageSizeOptions={[5, 10, 25, 50, 75, 100]}
          />
        </Box>
      )}
    </Box>
  );
}
